import {
    BoliaMoney,
    Certificate,
    DefaultImageType,
    ImageProperties,
    LightingSpecification,
    ProductDiscountType,
    ProductLabel,
    ProductLabelType,
    ProductPriceModel,
    ProductSpotLabel,
    ProductSpotModel,
} from '@/types/productServerApi';
import useGlobalLabels from '@/shared/content/useGlobalLabels';
import { GlobalLabels } from '@/shared/content/site-context.store';
const globalLabels = useGlobalLabels(GlobalLabels.product);
export default function useProductSpotLegacyDataMapping() {
    const legacyToNew = (legacyProductSpot: ProductSpotSearchResultLegacy): ProductSpotModel => {
        return {
            id: 0, // not defined in old product spot model
            sku: legacyProductSpot.id,
            name: legacyProductSpot.title,
            designerName: legacyProductSpot.designer,
            price: priceModel(legacyProductSpot),
            imageProperties: imageProperties(legacyProductSpot),
            quantityMultiplier: 1, // not defined in old product spot model
            lightingSpecification: lightingSpecification(legacyProductSpot),
            certificates: certificateList(legacyProductSpot),
            isPickAndPack: false, // not defined in old product spot model
            description: '', // not sure what this actually is
            labels: labels(legacyProductSpot),
            fromPrice: fromPrice(legacyProductSpot.fromPrice),
            ean: '',
            familyName: '',
            purchaseOptions: [],
            metaData: {
                businessArea: '',
                family: '',
                retailHierarchy: '',
                brand: '',
                ean: '',
            },
        };
    };

    const priceModel = (legacyProductSpot: ProductSpotSearchResultLegacy): ProductPriceModel => {
        const price: ProductPriceModel = {
            listPrice: {
                amount: legacyProductSpot.listPrice.amount,
                currency: legacyProductSpot.listPrice.currencyCode,
                rawAmount: legacyProductSpot.listPrice.raw.amount,
            },
            salesPrice: {
                amount: legacyProductSpot.salesPrice.amount,
                currency: legacyProductSpot.salesPrice.currencyCode,
                rawAmount: legacyProductSpot.salesPrice.raw.amount,
            },
            ecoTaxPrice: undefined,
            discount: undefined,
            priceValidUntil: undefined,
        };

        if (legacyProductSpot.ecoTax) {
            price.ecoTaxPrice = {
                amount: legacyProductSpot.ecoTax.raw.amount % 1 !== 0 ? legacyProductSpot.ecoTax.amountWithDecimals : legacyProductSpot.ecoTax.amount,
                currency: legacyProductSpot.ecoTax.currencyCode,
                rawAmount: legacyProductSpot.ecoTax.raw.amount,
            };
        }

        if (legacyProductSpot.discountText && legacyProductSpot.discountPercentage) {
            const isMyBoliaBoost = legacyProductSpot.discountType === 'MyBoliaBoost';
            price.discount = {
                type: isMyBoliaBoost ? ProductDiscountType.MyBoliaBoost : ProductDiscountType.Campaign,
                discount: {
                    amount: '',
                    rawAmount: legacyProductSpot.listPrice.raw.amount - legacyProductSpot.salesPrice.raw.amount,
                    currency: legacyProductSpot.salesPrice.currencyCode,
                },
                percentage: legacyProductSpot.discountPercentage.value,
                expiryLabel: undefined,
                discriminator: isMyBoliaBoost ? 'ProductMyBoliaBoostDiscount' : 'ProductCampaignDiscount',
            };
        }

        return price;
    };

    const fromPrice = (fromPrice: PriceModelLegacy): BoliaMoney | undefined => {
        if (!fromPrice) {
            return undefined;
        }
        return {
            amount: fromPrice.amount,
            currency: fromPrice.currencyCode,
            rawAmount: fromPrice.raw.amount,
        };
    };

    const lightingSpecification = (legacyProductSpot: ProductSpotSearchResultLegacy): LightingSpecification | undefined => {
        if (!legacyProductSpot.lightingSpecification) {
            return undefined;
        }

        return {
            specificationUrl: legacyProductSpot.lightingSpecification.specificationUrl,
            energyClassification: legacyProductSpot.lightingSpecification.energyClassification,
            energyClassificationUrl: legacyProductSpot.lightingSpecification.energyClassificationUrl,
        };
    };

    const certificateList = (legacyProductSpot: ProductSpotSearchResultLegacy): Certificate[] | undefined => {
        if (!legacyProductSpot.certifications) {
            return undefined;
        }

        return legacyProductSpot.certifications.map((certificate) => (
            {
                name: certificate.title,
                imageUrl: certificate.logoUrl,
                code: '',
                imageVersion: 0,
            }
        ));
    };

    const imageProperties = (legacyProductSpot: ProductSpotSearchResultLegacy): ImageProperties => {
        return {
            version: legacyProductSpot.imageVersion,
            type: legacyProductSpot.defaultImageType === 'Default' ? DefaultImageType.Default : DefaultImageType.Angle,
        };
    };

    const labels = (legacyProductSpot: ProductSpotSearchResultLegacy): ProductLabel[] | undefined => {
        const labelList: ProductLabel[] = [];
        if (legacyProductSpot.hasCustomizer) {
            labelList.push({
                type: ProductLabelType.Customize,
                text: globalLabels.splashLabelCustomize,
                backgroundColorHex: undefined,
            });
        }

        if (legacyProductSpot.isNewArrival) {
            labelList.push({
                type: ProductLabelType.NewArrival,
                text: globalLabels.splashLabelNew,
                backgroundColorHex: undefined,

            });
        }

        if (legacyProductSpot.productSpotLabel) {
            labelList.push({
                type: ProductLabelType.ProductSpot,
                text: legacyProductSpot.productSpotLabel.text,
                backgroundColorHex: legacyProductSpot.productSpotLabel.hexColorCode,
            });
        }

        return labelList.length ? labelList : undefined;
    };

    return {
        legacyToNew,
    };
}

export interface ProductSpotSearchResultLegacy {
    discountProperties: {
        familyCode: string;
        businessAreaId: string;
        seasonCode: string;
        collectionId: string;
        retailHierarchyId: string;
    };
    discountText?: string;
    discountPercentage?: { value: number };
    discountType: 'MyBoliaBoost' | 'Campaign' | string;
    designer: string;
    ecoTax?: PriceModelLegacy;
    listPrice: PriceModelLegacy;
    salesPrice: PriceModelLegacy;
    id: string;
    isNewArrival: boolean;
    fromPrice: PriceModelLegacy;
    defaultImageType: string;
    imageScale: number;
    designInformation: string;
    imageVersion: number;
    hasCustomizer: boolean;
    lightingSpecification?: LightingCertification;
    certifications?: [
        {
            title: string;
            logoUrl: string
        }
    ];
    productSpotLabel: ProductSpotLabel;
    type: string; // 'catalogresult'
    title: string;
    metaData?: {
        brand?: string;
        businessArea?: string;
        retailHierarchy?: string;
        family?: string;
    }
}

interface PriceModelLegacy {
    raw: {
        amount: number;
        currency: string;
    };
    amount: string;
    amountWithDecimals: string;
    currencyCode: string;
}

interface LightingCertification {
    energyClassification: string;
    energyClassificationUrl: string;
    specificationUrl: string;
}
